import { useEffect, useRef } from 'react';

/**
 * Calls `fn` immediately and then every `intervalMs` while `enabled`.
 * Overlapping calls are skipped and a call finishing after the hook was
 * disabled or unmounted is ignored.
 */
export const usePolling = (
  fn: (signal: { cancelled: boolean }) => Promise<void>,
  intervalMs: number,
  enabled: boolean,
) => {
  const fnRef = useRef(fn);

  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;

    const signal = { cancelled: false };
    let inFlight = false;

    const tick = async () => {
      if (inFlight || signal.cancelled) return;

      inFlight = true;

      try {
        await fnRef.current(signal);
      } finally {
        inFlight = false;
      }
    };

    tick();

    const timer = window.setInterval(tick, intervalMs);

    return () => {
      signal.cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled, intervalMs]);
};
